import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { NavigationEnd, Router } from '@angular/router';

import { provideComponentStore } from '@ngrx/component-store';
import { Extraction } from 'projects/semeris/src/generated/graphql-models';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NgSelectComponent } from '../../../shared/components/ng-select/ng-select.component';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { ButtonDirective } from '../../../ui/button/button/button.directive';
import { DependencyTreeItemComponent } from '../../components/dependency-tree-item/dependency-tree-item.component';
import { DependencyType } from '../../models/dependency-tree-item.model';
import {
  DependencyTreeStore,
  LayoutType,
  TreeItemViewModel,
} from '../../store/dependency-tree.store';

export interface DependencyTreeInput {
  id: string;
  type: DependencyType;
}

@Component({
  selector: 'semeris-dependency-tree-container',
  imports: [
    CommonModule,
    DependencyTreeItemComponent,
    LoaderComponent,
    MatIconModule,
    MatDialogModule,
    PermissionsDirective,
    NgSelectComponent,
    MatListModule,
    ButtonDirective,
  ],
  templateUrl: './dependency-tree-container.component.html',
  styleUrls: ['./dependency-tree-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideComponentStore(DependencyTreeStore)],
})
export class DependencyTreeContainerComponent {
  data = inject<DependencyTreeInput>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<DependencyTreeInput>>(MatDialogRef);
  private readonly store = inject(DependencyTreeStore);
  private readonly router = inject(Router);

  public viewModels$: Observable<TreeItemViewModel[]> = this.store.viewModels$;
  public loading$: Observable<boolean> = this.store.loading$;
  public transactions$ = this.store.transactions$;
  public dependencyGraphs$ = this.store.dependencyGraphs$;
  public extractions$ = this.store.extractions$;
  public datapointHistoryItems$ = this.store.datapointHistoryItems$;
  public selectedExtraction$ = this.store.selectedExtraction$;
  public noExtractions$ = this.store.noExtractions$;

  public readonly gridSize = 100;
  public readonly LayoutType = LayoutType;
  public readonly Permission = Permission;

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.store.destroy$)
      )
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  public onExtractionHistoryChange(event: MatSelectionListChange) {
    const selectedItem = event.options[0]?.value as Extraction;
    this.store.selectedExtraction(selectedItem.datapointHash);
  }

  public ngOnInit() {
    this.store.setParams(this.data.id, this.data.type);
  }

  public onEditMetadata(item: TreeItemViewModel) {
    this.store.onEditMetadata(item);
  }

  public onChangeActive(item: TreeItemViewModel) {
    this.store.onChangeActive(item);
  }

  public onLibraryTransactionChange(event: string | string[]) {
    this.store.libraryTransactionId(Array.isArray(event) ? event[0] : event);
  }

  public onDependencyGraphChange(event: string | string[]) {
    this.store.dependencyGraphId(Array.isArray(event) ? event[0] : event);
  }
}
